import React, { useEffect, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import Icon from '../../Icons/Icon';
import useValidatePassword from './useValidatePassword';

const PasswordInput = (
  {
    name,
    label,
    value = '',
    setValue = () => {},
    isRequired = false,
    errorMessage = '',
    onKeyEnterPressed,
    placeholder,
    isValid = true,
    isNewPassword = false,
    extraInfo,
    className = '',
    shouldHideConfirmedIcon = false,
    fontSize = 'text-base',
    labelMargin = 'mb-2',
    validationErrorMessage,
    isValidationBorderEnabled = false,
    labelCustomStyle = '',
    dataTestId = undefined,
    ...props
  },
) => {
  const inputRef = useRef(null);
  const [isPasswordInputFocused, setIsPasswordInputFocused] = useState(false);
  const [isPasswordShown, setIsPasswordShown] = useState(false);
  const [isCapsLockOn, setIsCapsLockOn] = useState(false);

  const validation = useValidatePassword(value);
  const onChange = (e) => {
    setValue(e.target.value);
  };
  /**
   * Handle Enter key pressed
   */
  const handleEnterKeyPressed = (e) => {
    if (onKeyEnterPressed && e.code === 'Enter') {
      onKeyEnterPressed();
    }
  };
  // This function checks if all the validations are true
  const checkPassword = () => {
    return Object.values(validation).every(val => val === true);
  };

  const handleClick = (e) => {
    e.preventDefault();
    setIsPasswordShown(prev => !prev); // Toggle password visibility
  };
  // useEffect to handle focus and cursor position restoration after password visibility change
  useEffect(() => {
    const input = inputRef.current;
    if (input && document.activeElement === input) {
      const cursorPos = input.selectionStart; // Save the current cursor position
      input.focus(); // Ensure the input remains focused
      input.setSelectionRange(cursorPos, cursorPos); // Restore cursor position
    }
  }, [isPasswordShown]);
  const getBorderColor = () => {
    const isEmpty = value === '';
    const requiredError = isRequired && isEmpty;
    const passwordError = isNewPassword && !checkPassword() && !isEmpty;
    const genericError = !isNewPassword && !isValid;
    const hasError = requiredError || passwordError || genericError;
    return hasError ? 'border-vamos-red' : '';
  };

  const getErrorBorder = () => {
    if (isValidationBorderEnabled && ['Required', 'Invalid'].includes(validationErrorMessage)) return 'border-vamos-red';
    return '';
  };

  return (
    <div {...props} className="flex-column">
      {label && (
        <div className={`flex items-center space-x-1.5 ${labelCustomStyle && labelCustomStyle}`}>
          <label className="text-vamos-black font-sans text-lg font-medium flex space-x-1.5">
            <span>{label}</span>
            {isRequired && <Icon name="required_field" className="w-2" />}
          </label>
        </div>
      )}
      <p className={`whitespace-nowrap text-base text-vamos-gray font-medium ${labelMargin}`}>{extraInfo}</p>
      <div className="flex items-center space-x-2">
        <div className="flex relative w-full md:w-77">
          <input
            ref={inputRef}
            // Here we have two similar lines for different situations to give the red border, I think is better like this for clarity.
            className={`
            border border-vamos-blue-gray
            ${!isPasswordInputFocused && (value !== null) && getBorderColor()} 
            ${getErrorBorder()}
            rounded-md bg-vamos-white h-10 focus:border-vamos-black py-1.5 px-3 focus:ring-0 w-full outline-none ${className} ${fontSize}
            placeholder-vamos-gray
          `}
            type={isPasswordShown ? 'text' : 'password'}
            name={name}
            value={value}
            required={isRequired}
            placeholder={placeholder}
            onChange={onChange}
            onKeyDown={(e) => {
              handleEnterKeyPressed(e);
              setIsCapsLockOn(e.getModifierState && e.getModifierState('CapsLock'));
            }}
            onKeyUp={(e) => {
              setIsCapsLockOn(e.getModifierState && e.getModifierState('CapsLock'));
            }}
            onFocus={() => setIsPasswordInputFocused(true)}
            onBlur={() => { setIsPasswordInputFocused(false); setIsCapsLockOn(false); }}
            data-testid={dataTestId}
          />
          <button
            type="button"
            // In this case we use onMouseDown instead of onClick to keep focus on the input after show/hide password visibility
            onMouseDown={handleClick}
          >
            <Icon name={isPasswordShown ? 'hide_password' : 'visibility'} className="absolute right-3 top-3" />
          </button>
        </div>
        {((isNewPassword && checkPassword()) || (!isNewPassword && value && isValid)) && !shouldHideConfirmedIcon && (
          <Icon name="confirmed" />
        )}
      </div>
      {isCapsLockOn && (
        <div className="flex items-center mt-1 space-x-1 text-vamos-black text-sm font-normal">
          <span>Caps lock is on</span>
        </div>
      )}
      {/* Here we show a message when the password is ivalid. */}
      {errorMessage && !isPasswordInputFocused && ((isNewPassword && (value !== null) && !checkPassword()) || (!isNewPassword && !isValid))
        && (
          <div className="flex items-center mt-2 mb-2 whitespace-nowrap space-x-1.5">
            <p type="invalid" className="text-vamos-red text-sm font-normal">
              {errorMessage}
            </p>
          </div>
        )}
      {validationErrorMessage && (
        <h4 type="invalid" className={`text-vamos-red font-normal text-sm py-1 ${isValidationBorderEnabled ? 'absolute' : ''}`}>
          {validationErrorMessage}
        </h4>
      )}
      {/* All this section is to show the new password validation list */}
      {isNewPassword && (
        <>
          {((value && !checkPassword()) || isPasswordInputFocused) && (
            <ul className="py-2">
              <li className={
                `${!validation.number && isPasswordInputFocused && 'list-disc ml-6'} 
            ${validation.number && 'text-vamos-green1'}
            ${!validation.number && !isPasswordInputFocused && 'text-vamos-red'}`
              }
              >
                <div className="flex">
                  {validation.number && <Icon name="check" />}
                  {!validation.number && !isPasswordInputFocused && <Icon name="not_check" />}
                  <p>1 number</p>
                </div>
              </li>
              <li className={
                `${!validation.uppercase && isPasswordInputFocused && 'list-disc ml-6'} 
            ${validation.uppercase && 'text-vamos-green1'}
            ${!validation.uppercase && !isPasswordInputFocused && 'text-vamos-red'}`
              }
              >
                <div className="flex">
                  {validation.uppercase && <Icon name="check" />}
                  {!validation.uppercase && !isPasswordInputFocused && <Icon name="not_check" />}
                  <p>1 uppercase</p>
                </div>
              </li>
              <li className={
                `${!validation.lowercase && isPasswordInputFocused && 'list-disc ml-6'} 
            ${validation.lowercase && 'text-vamos-green1'}
            ${!validation.lowercase && !isPasswordInputFocused && 'text-vamos-red'}`
              }
              >
                <div className="flex">
                  {validation.lowercase && <Icon name="check" />}
                  {!validation.lowercase && !isPasswordInputFocused && <Icon name="not_check" />}
                  <p>1 lowercase</p>
                </div>
              </li>
              <li className={
                `${!validation.specialCharacter && isPasswordInputFocused && 'list-disc ml-6'} 
            ${validation.specialCharacter && 'text-vamos-green1'}
            ${!validation.specialCharacter && !isPasswordInputFocused && 'text-vamos-red'}`
              }
              >
                <div className="flex">
                  {validation.specialCharacter && <Icon name="check" />}
                  {!validation.specialCharacter && !isPasswordInputFocused && <Icon name="not_check" />}
                  <p>1 special character</p>
                </div>
              </li>
              <li className={
                `${!validation.minimumCharacters && isPasswordInputFocused && 'list-disc ml-6'} 
            ${validation.minimumCharacters && 'text-vamos-green1'}
            ${!validation.minimumCharacters && !isPasswordInputFocused && 'text-vamos-red'}`
              }
              >
                <div className="flex">
                  {validation.minimumCharacters && <Icon name="check" />}
                  {!validation.minimumCharacters && !isPasswordInputFocused && <Icon name="not_check" />}
                  <p>6 characters minimum</p>
                </div>
              </li>
            </ul>
          )}
          {/* This guide is shown before any character is type */}
          {!isPasswordInputFocused && !value && (
            <p className="text-sm w-full md:w-80 mt-2 mx-[3px]">
              Password must be at least 6 characters and contain at least 1 uppercase, 1 lowercase, 1 number and 1 special character.
            </p>
          )}
        </>
      )}
    </div>
  );
};

PasswordInput.displayName = 'PasswordInput';

PasswordInput.propTypes = {
  label: PropTypes.string,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  setValue: PropTypes.func,
  isRequired: PropTypes.bool,
  errorMessage: PropTypes.string,
  onKeyEnterPressed: PropTypes.func,
  placeholder: PropTypes.string,
  isValid: PropTypes.bool,
  className: PropTypes.string,
  isNewPassword: PropTypes.bool,
  extraInfo: PropTypes.string,
  shouldHideConfirmedIcon: PropTypes.bool,
  fontSize: PropTypes.string,
  labelMargin: PropTypes.string,
  validationErrorMessage: PropTypes.string,
  isValidationBorderEnabled: PropTypes.bool,
  labelCustomStyle: PropTypes.string,
  dataTestId: PropTypes.string,
};

export default PasswordInput;
